import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

export type AiErrorType =
  'rate-limit' | 'network' | 'auth' | 'forbidden' | 'generic';

// The IQ Boost generate-response stream uses fetch, which surfaces failures as
// plain Errors (message text), not HttpErrorResponse — classify both shapes.
export function classifyAiError(error: unknown): AiErrorType {
  if (
    error instanceof Error &&
    (error.name === 'TimeoutError' ||
      error.message === 'Request timed out. Please try again.')
  ) {
    return 'network';
  }

  if (!(error instanceof HttpErrorResponse)) {
    if (error instanceof Error) {
      return classifyStreamError(error);
    }
    return 'generic';
  }

  if (error.status === HttpStatusCode.TooManyRequests) return 'rate-limit';
  if (error.status === HttpStatusCode.Unauthorized) return 'auth';
  // 403 is the IQ Boost feature gate, not an expired session — fail closed.
  if (error.status === HttpStatusCode.Forbidden) return 'forbidden';
  if (error.status === 0) return 'network';

  // iqboost wraps Azure 429 errors as HTTP 500 with raw Azure JSON as the body
  if (error.status === HttpStatusCode.InternalServerError) {
    const body =
      typeof error.error === 'string'
        ? error.error
        : JSON.stringify(error.error ?? '');
    if (body.includes('"429"') || body.toLowerCase().includes('rate limit')) {
      return 'rate-limit';
    }
  }

  return 'generic';
}

function classifyStreamError(error: Error): AiErrorType {
  const message = error.message.toLowerCase();

  if (message.includes('status 403')) {
    return 'forbidden';
  }
  if (
    message.includes('unable to refresh the token') ||
    message.includes('status 401')
  ) {
    return 'auth';
  }
  if (message.includes('429') || message.includes('rate limit')) {
    return 'rate-limit';
  }
  if (error.name === 'TypeError' || message.includes('failed to fetch')) {
    return 'network';
  }
  return 'generic';
}

export function canRetryAiError(errorType: AiErrorType): boolean {
  return errorType === 'rate-limit' || errorType === 'network';
}

export function getAiErrorTranslationKey(errorType: AiErrorType): string {
  switch (errorType) {
    case 'rate-limit':
      return 'outlook.iqBoost.error.rateLimit';
    case 'network':
      return 'outlook.iqBoost.error.network';
    case 'auth':
      return 'outlook.iqBoost.error.auth';
    case 'forbidden':
      return 'outlook.iqBoost.error.forbidden';
    default:
      return 'outlook.iqBoost.error.generic';
  }
}
